/**
 * PHENOTYPE — the rendering endpoint for biological expression.
 *
 * This module is the shared CONCEPT, not a shared anatomy: plants and animals
 * keep their own axis sets (`PlantPhenotype`, `WildlifePhenotype`) and their
 * own geometry machinery, and what they share is the conventions —
 *
 *   · **0 is ordinary.** Every axis is a normalized deviation from the
 *     kind's baseline; a missing axis, a missing phenotype, or a missing
 *     source all mean *exactly* the organism the world draws today.
 *   · **±1 is the natural range** — the span ordinary biology is expected to
 *     occupy once a simulation supplies these values.
 *   · **±2 is the hard clamp** (`PHENO_LIMIT`). The headroom above natural is
 *     deliberate: the trait standing ladder (Ordinary → … → Mythic) will
 *     eventually want expression *beyond* ordinary variation, and the clamp
 *     is where "extraordinary" lives without new machinery. Nothing renders
 *     nonsense at the clamp — every consumer turns a value into a bounded
 *     multiplicative factor via `expressed()`.
 *   · **Expression is multiplicative and exact at neutral.** `expressed(v, g)`
 *     is `g^v`: `pow(g, 0) === 1` to the bit, so a neutral phenotype composes
 *     into every scale and colour as a multiply-by-one and the drawn world is
 *     bit-identical with the phenotype machinery in place. That is the whole
 *     backwards-compatibility argument, and `test/phenotype.ts` holds it.
 *
 * WHAT THIS IS NOT. There is no trait simulation here, no inheritance, no
 * standing ladder, and no persistence — see `world/strain.ts` for the (inert,
 * complete) trait substrate this layer was shaped to eventually receive:
 * `StrainTraits` are multipliers on *process* coefficients, and a future
 * adapter maps them (plus environment, age, divine pressure — whatever the
 * simulation decides expression is) onto these *visual* axes. The renderer
 * deliberately cannot tell where a value came from; it receives the FINAL
 * EXPRESSED phenotype and draws it. Until something in `src/sim` supplies
 * one, every source is dev-only (`ui/phenoLab.ts`) and the shipped world
 * renders neutral — `WIRING.md` carries the row.
 *
 * Individual variation stays where it already lives. The world's per-plant
 * draws (`grow`, `wide`, `vigour`, tone, shade, hue in `world/chunk.ts`) and
 * the per-body hashes (`render/wildlife.ts`) are the "small seeded deviation"
 * of the brief; phenotype composes WITH them, multiplicatively, and at ±1 it
 * clears them — a +1 population reads as different from a neutral one, not as
 * its far tail (`test/phenotype.ts` measures the dominance per axis).
 */

/** The hard clamp on any normalized axis value. ±1 is natural; the headroom
 *  to ±2 is reserved for future extraordinary/Mythic expression. */
export const PHENO_LIMIT = 2;

/** Clamp a normalized axis value into the representable range. NaN → 0. */
export const clampPheno = (v: number): number =>
  Number.isNaN(v) ? 0 : v < -PHENO_LIMIT ? -PHENO_LIMIT : v > PHENO_LIMIT ? PHENO_LIMIT : v;

/**
 * A normalized value → a multiplicative factor. `gain` is the factor at +1;
 * −1 gives its reciprocal, 0 gives exactly 1. Gains stay near 1 on purpose:
 * even at the ±2 clamp a 1.45 gain is ×0.48..×2.10, bounded and finite.
 */
export const expressed = (v: number, gain: number): number =>
  Math.pow(gain, clampPheno(v));

/**
 * PLANT phenotype — eight meaningful axes, chosen against the audit of
 * `render/vegetation.ts` rather than against a wishlist. Each names the
 * biological direction; what geometry it moves is per kind
 * (`PLANT_EXPRESSION` in vegetation.ts), and a kind it cannot mean anything
 * for simply carries a zero gain there.
 *
 * Four are per-INDIVIDUAL (they ride the instance matrix and colour, so every
 * plant can carry its own value): `stature`, `breadth`, `light`, `warmth`.
 * Four are structural and per-STAND (they ride a per-mesh uniform over the
 * shared build — the chunk-mesh grain; see `chunkView.ts` for why per-instance
 * structure is deliberately not bought at this tier): `crownLift`,
 * `foliageDensity`, `droop`, `trunkMass`.
 */
export interface PlantPhenotype {
  /** Taller ↔ shorter, uniformly — the strongest silhouette axis. */
  stature: number;
  /** Broader ↔ narrower crown/plant envelope (the instance's own XZ). */
  breadth: number;
  /** Crown carried high on a longer clear trunk ↔ slung low. Internal
   *  redistribution: the build's top does not move (stature owns height). */
  crownLift: number;
  /** Denser ↔ sparser foliage masses about their own centres. On the kinds
   *  whose foliage IS reproductive mass (flower, umbel, candle heads) this is
   *  also the bloom-abundance endpoint — deliberately, not accidentally. */
  foliageDensity: number;
  /** Weeping ↔ upswept: foliage (and what carries it) sinks with distance
   *  from the axis. A willow reads it strongly, a succulent not at all. */
  droop: number;
  /** Heavier ↔ lighter wood: the bole thickens, fading with height so limb
   *  tips stay seated in their masses. */
  trunkMass: number;
  /** Pigment lightness: paler ↔ darker foliage. */
  light: number;
  /** Pigment warmth: warmer (red-shifted) ↔ cooler (blue-shifted) foliage. */
  warmth: number;

  /* ---- the GENERATION tier (2026-08-20) ------------------------------
   *
   * Two axes that change how many branches a plant HAS and how far they
   * reach, which is a different kind of axis from the eight above and the
   * difference is worth stating.
   *
   * The eight above are EXPRESSION: they ride the instance matrix, the
   * instance colour or a per-mesh uniform, so they re-dress a standing mesh
   * and cost nothing when neutral. These two cannot — a branch that does not
   * exist has no vertices to move — so they are read at GENERATION, by
   * `render/tree/grow.ts`, and a plant wearing them is a plant that was grown
   * differently. The static kit in `render/vegetation.ts` cannot honour them
   * at all and ignores them, which is correct rather than a gap: its builds
   * are authored, and you cannot morph a build into having more limbs.
   */
  /** More ↔ fewer primary limbs, and orders below them. */
  branchCount: number;
  /** Longer ↔ shorter branches at every order — reach, not crown width. */
  branchLength: number;
}

/** WILDLIFE phenotype — nine axes against the quadruped the audit found. */
export interface WildlifePhenotype {
  /** Larger ↔ smaller animal, uniformly. */
  stature: number;
  /** Longer ↔ shorter body along the spine (nose-to-rump stretches). */
  bodyLength: number;
  /** Heavier ↔ leaner: barrel depth, stance width and leg thickness — never
   *  height (that is stature's or legLength's). */
  bodyMass: number;
  /** Longer ↔ shorter legs, hoof planted, hip rising — the existing leg
   *  channel, deepened. */
  legLength: number;
  /** Longer ↔ shorter neck along its own axis; the head rides out with it. */
  neckLength: number;
  /** Larger ↔ smaller head (horns ride the skull). */
  headScale: number;
  /** Horn/antler expression, on the kinds that carry one. A kind with no
   *  appendage (`SubKind.horn === 0`) reads this as nothing — the honest
   *  no-op the brief allows. */
  appendageScale: number;
  /** Coat lightness: paler ↔ darker. */
  light: number;
  /** Coat warmth: warmer ↔ colder. */
  warmth: number;
}

export const NEUTRAL_PLANT: Readonly<PlantPhenotype> = Object.freeze({
  stature: 0, breadth: 0, crownLift: 0, foliageDensity: 0, droop: 0,
  trunkMass: 0, light: 0, warmth: 0,
  branchCount: 0, branchLength: 0,
});

export const NEUTRAL_WILDLIFE: Readonly<WildlifePhenotype> = Object.freeze({
  stature: 0, bodyLength: 0, bodyMass: 0, legLength: 0, neckLength: 0,
  headScale: 0, appendageScale: 0, light: 0, warmth: 0,
});

/** Fill a partial phenotype against neutral, clamping every axis. */
export function resolvePlant(p?: Partial<PlantPhenotype> | null): PlantPhenotype {
  if (!p) return { ...NEUTRAL_PLANT };
  const out = { ...NEUTRAL_PLANT } as PlantPhenotype;
  for (const k of Object.keys(NEUTRAL_PLANT) as (keyof PlantPhenotype)[]) {
    const v = p[k];
    if (v !== undefined) out[k] = clampPheno(v);
  }
  return out;
}

/** Same, for animals. */
export function resolveWildlife(p?: Partial<WildlifePhenotype> | null): WildlifePhenotype {
  if (!p) return { ...NEUTRAL_WILDLIFE };
  const out = { ...NEUTRAL_WILDLIFE } as WildlifePhenotype;
  for (const k of Object.keys(NEUTRAL_WILDLIFE) as (keyof WildlifePhenotype)[]) {
    const v = p[k];
    if (v !== undefined) out[k] = clampPheno(v);
  }
  return out;
}

/**
 * Compose an inherited/population value with a small individual deviation —
 * the brief's `final = baseline + phenotype + seeded deviation`, on the
 * normalized scale, clamped once at the end. The renderer's existing jitter
 * channels are NOT routed through this today (they predate phenotype and
 * already read well); it exists so a future simulation that wants to deal
 * per-individual deviation *on the phenotype axes themselves* has the one
 * obvious place to do it.
 */
export const composePheno = (population: number, deviation: number): number =>
  clampPheno(clampPheno(population) + deviation);

/**
 * A plant phenotype source: the renderer's one question to whatever will
 * eventually know — "what is expressed HERE, for this kind?" Pure in its
 * arguments (law 1 discipline even though this is render-side: two machines
 * looking at the same ground must draw the same forest). `null` means
 * ordinary, and costs nothing downstream.
 *
 * The position is the plant's own; a population-grain source (a strain field,
 * a region cell) simply answers coarsely. Age/life-stage note: chunk plants
 * carry no age today (`grow` is size-within-species, not a life stage), so
 * there is nothing for stature to accidentally bypass; if generation ever
 * grows a real age, it belongs in the SOURCE (expressed phenotype), not here.
 */
export type PlantPhenotypeSource =
  (kind: string, x: number, z: number) => Partial<PlantPhenotype> | null;
